import { supabase } from '@/lib/supabaseClient';
import { MortalityEventRecord } from '@/types/database';
import { documentService } from '@/services/documentService';

export interface LogMortalityPayload {
  cycle_id: string;
  event_date: string;
  head_count: number;
  total_weight_kg?: number | null;
  cause_of_death?: string | null;
  production_unit_id?: string | null;
  cost_booked?: number;
  disposal_method?: string | null;
  notes?: string | null;
}

export const mortalityService = {
  /**
   * Common causes of death suggestions for typeahead.
   * Flexible standard: arbitrary custom text is fully allowed by schema.
   */
  getSuggestedCauses(): string[] {
    return [
      'Respiratory Disease / Pneumonia',
      'Digestive / Scours / Diarrhea',
      'Heat Stress / Stroke',
      'Physical Trauma / Crushing',
      'Sudden Death Syndrome',
      'Rectal Prolapse',
      'Meningitis / Strep suis',
      'Suspected ASF / Viral Symptoms',
      'Other / Unspecified',
    ];
  },

  /**
   * Common disposal methods suggestions for typeahead.
   * Flexible standard: arbitrary custom text is fully allowed.
   */
  getSuggestedDisposalMethods(): string[] {
    return [
      'Deep Burial with Lime',
      'Composting Facility',
      'Incineration',
      'Rendering / Offsite Disposal',
      'Other',
    ];
  },

  /**
   * Fetches all non-void mortality events for a cycle with documents.
   */
  async getMortalityByCycle(cycleId: string): Promise<MortalityEventRecord[]> {
    const { data, error } = await supabase
      .from('mortality_events')
      .select(`
        *,
        production_units (id, unit_code, unit_name)
      `)
      .eq('cycle_id', cycleId)
      .eq('is_void', false)
      .order('event_date', { ascending: false });

    if (error) {
      console.error('[mortalityService.getMortalityByCycle] Error:', error.message);
      throw error;
    }

    const events = (data || []) as MortalityEventRecord[];

    // Fetch documents for each mortality event
    for (const event of events) {
      try {
        const docs = await documentService.getEntityDocuments('mortality_events', event.id);
        event.documents = docs;
      } catch (e) {
        event.documents = [];
      }
    }

    return events;
  },

  /**
   * Logs a mortality event with optional photo attachment.
   */
  async logMortality(
    payload: LogMortalityPayload,
    attachmentFile?: File | null
  ): Promise<MortalityEventRecord> {
    if (!payload.cycle_id) {
      throw new Error('Cycle ID is required.');
    }
    if (!payload.event_date) {
      throw new Error('Event date is required.');
    }
    if (!payload.head_count || payload.head_count <= 0) {
      throw new Error('Head count must be greater than zero.');
    }

    const insertData: any = {
      cycle_id: payload.cycle_id,
      event_date: payload.event_date,
      head_count: payload.head_count,
      total_weight_kg: payload.total_weight_kg ? Number(payload.total_weight_kg) : null,
      cause_of_death: payload.cause_of_death?.trim() || null,
      production_unit_id: payload.production_unit_id || null,
      cost_booked: Number(payload.cost_booked) || 0,
      disposal_method: payload.disposal_method?.trim() || null,
      notes: payload.notes?.trim() || null,
    };

    const { data: event, error: eventErr } = await supabase
      .from('mortality_events')
      .insert(insertData)
      .select('*')
      .single();

    if (eventErr || !event) {
      console.error('[mortalityService.logMortality] Insert error:', eventErr?.message);
      throw new Error(`Failed to log mortality event: ${eventErr?.message}`);
    }

    if (attachmentFile) {
      try {
        await documentService.uploadDocument({
          file: attachmentFile,
          fileName: attachmentFile.name,
          entityType: 'mortality_events',
          entityId: event.id,
          documentType: 'photo',
          notes: 'Mortality carcass / disposal photo',
        });
      } catch (docErr: any) {
        console.warn('[mortalityService.logMortality] Document upload warn:', docErr.message);
      }
    }

    return event as MortalityEventRecord;
  },
};
